import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import axios from "axios";

function hasStoredSessionHint(user) {
  if (user) return true;
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return Boolean(parsed && typeof parsed === "object");
  } catch {
    return false;
  }
}

export const UserContext = createContext({
  user: null,
  setUser: () => {},
  refreshUser: async () => null,
});

export const useUser = () => {
  const ctx = useContext(UserContext);
  return ctx.user;
};

export const useSetUser = () => {
  const ctx = useContext(UserContext);
  return ctx.setUser;
};

/** Re-fetch session from `/api/auth/me` (roles, permissions, profile). */
export const useRefreshUser = () => {
  const ctx = useContext(UserContext);
  return ctx.refreshUser;
};

export const UserProvider = ({ user: initialUser, children }) => {
  const [user, setUser] = useState(initialUser);
  /** Bumps whenever auth state is set explicitly; ignores stale in-flight `/api/auth/me` from before login/logout. */
  const sessionGenerationRef = useRef(0);

  // Keep localStorage in sync
  const setUserAndStorage = useCallback((u) => {
    sessionGenerationRef.current += 1;
    setUser(u);
    if (u) {
      localStorage.setItem("user", JSON.stringify(u));
    } else {
      localStorage.removeItem("user");
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const startedAt = sessionGenerationRef.current;
    try {
      const res = await axios.get("/api/auth/me", { withCredentials: true });
      if (sessionGenerationRef.current !== startedAt) return null;
      if (res.data?.user) {
        setUserAndStorage(res.data.user);
        return res.data.user;
      }
      setUserAndStorage(null);
      return null;
    } catch {
      if (sessionGenerationRef.current !== startedAt) return null;
      setUserAndStorage(null);
      return null;
    }
  }, [setUserAndStorage]);

  useEffect(() => {
    if (!hasStoredSessionHint(initialUser)) return undefined;

    let cancelled = false;
    const startedAt = sessionGenerationRef.current;
    const syncSession = async () => {
      try {
        const res = await axios.get("/api/auth/me", { withCredentials: true });
        if (cancelled) return;
        if (sessionGenerationRef.current !== startedAt) return;
        if (res.data?.user) {
          setUserAndStorage(res.data.user);
        } else {
          setUserAndStorage(null);
        }
      } catch {
        if (cancelled) return;
        if (sessionGenerationRef.current !== startedAt) return;
        setUserAndStorage(null);
      }
    };
    syncSession();
    return () => {
      cancelled = true;
    };
  }, [initialUser, setUserAndStorage]);

  const value = useMemo(
    () => ({ user, setUser: setUserAndStorage, refreshUser }),
    [user, setUserAndStorage, refreshUser],
  );
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
